import { designVariables } from "@studio3t/design-variables";
import { Icon, IconSVG } from "@studio3t/generic-components";
import type { ReactNode } from "react";
import styled, { useTheme } from "styled-components";

const TABLE = designVariables.ui.table;

export type SortDirection = "asc" | "desc" | null;

// Sort affordance per Figma 13078-31995: each sortable header carries a 10px
// arrow. It is hidden until the header is hovered, and stays visible once
// the column is the active sort. Active sort: ascending points up,
// descending points down (the standard table convention, down = high→low).
// While unsorted, the hover preview points up because the first click sorts
// ascending. (Icon.Bottom points down at rest, so descending = no rotation and
// ascending/unsorted = 180°.) Keyboard focus shows no preview: the focus
// ring alone marks the header, and the arrow appears with the first sort —
// a preview on focus read as "already sorted ascending" (TTTL-186). Shared
// by the Pipelines list and the Workers table, which drive sorting
// differently (TanStack vs a hand-rolled list) but render the same control.
const SortHeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${TABLE.thead.cell.gap}px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  text-align: inherit;
  cursor: pointer;
`;

const SortArrow = styled.span<{ $visible: boolean; $descending: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
  transform: rotate(${(props) => (props.$descending ? 0 : 180)}deg);

  ${SortHeaderButton}:hover & {
    opacity: 1;
  }
`;

export function SortableHeader({
  direction,
  onClick,
  children,
}: {
  direction: SortDirection;
  onClick?: () => void;
  children: ReactNode;
}) {
  const theme = useTheme();
  return (
    <SortHeaderButton type="button" onClick={onClick}>
      {children}
      <SortArrow
        $visible={direction !== null}
        $descending={direction === "desc"}
      >
        <IconSVG
          icon={Icon.Bottom}
          size={10}
          colors={{ default: theme.semanticTextLight }}
        />
      </SortArrow>
    </SortHeaderButton>
  );
}
